import React, { useState, useEffect, useId, useRef } from "react";
import { Box, ButtonBase, CircularProgress, Collapse, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AgentAvatar from "../AgentAvatar";
import MarkdownContent from "../MarkdownContent";
import { AVATAR_SIZE, AVATAR_RADIUS, TEXT_INDENT } from "./conversationConstants";
import {
  messageRowSx,
  formatDateLabel,
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
} from "./conversationUtils";

export function DateDivider({ dateStr }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: 2.5, my: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
      <Typography
        sx={{
          px: 1.5,
          fontSize: "12px",
          fontWeight: 600,
          color: "text.secondary",
          whiteSpace: "nowrap",
          lineHeight: 1,
        }}
      >
        {formatDateLabel(dateStr)}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
    </Box>
  );
}

export function deriveToolLabelFromParts(typedMessages) {
  if (!Array.isArray(typedMessages) || typedMessages.length === 0) return null;
  const lastMsg = typedMessages[typedMessages.length - 1];
  if (!lastMsg || lastMsg.status === "done") return null;
  const activeParts = (lastMsg.parts || []).filter(
    (p) => p.type === "tool" && (p.state === "input-streaming" || p.state === "input-available")
  );
  if (activeParts.length === 0) return null;
  const toolName = activeParts[activeParts.length - 1].toolName;
  return toolName === "unknown_tool" ? null : toolName;
}

function formatElapsed(totalSeconds) {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
}

function useElapsedSeconds() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      setSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return seconds;
}

function parseTimestampMs(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function formatPartElapsed(startedAt, endedAt) {
  const startMs = parseTimestampMs(startedAt);
  const endMs = parseTimestampMs(endedAt);
  if (startMs == null || endMs == null || endMs < startMs) return null;
  return formatElapsed(Math.max(1, Math.round((endMs - startMs) / 1000)));
}

export function ReasoningBlock({
  content,
  state,
  startedAt,
  endedAt,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings,
}) {
  const isActive = state === "streaming";
  const [expanded, setExpanded] = useState(isActive);
  const bodyRef = useRef(null);
  const bodyId = useId();
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const elapsedLabel = formatPartElapsed(startedAt, endedAt);
  const label = isActive ? "Thinking" : elapsedLabel ? `Thought for ${elapsedLabel}` : "Thought";

  useEffect(() => {
    if (isActive) {
      setExpanded(true);
      return undefined;
    }
    const id = setTimeout(() => setExpanded(false), 1000);
    return () => clearTimeout(id);
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !expanded || !bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [content, expanded, isActive]);

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar
            name={agentName}
            size={AVATAR_SIZE}
            onClick={avatarClick}
            sx={getClickableAgentAvatarSx(Boolean(avatarClick))}
          />
        )}
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: AVATAR_RADIUS,
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <ButtonBase
            component="button"
            type="button"
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={() => setExpanded((value) => !value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              width: "100%",
              px: 1.25,
              py: 0.75,
              backgroundColor: "action.hover",
              cursor: "pointer",
              textAlign: "left",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: isActive ? "primary.main" : "text.disabled",
                animation: isActive ? "agentPulse 1.4s infinite ease-in-out" : "none",
                "@keyframes agentPulse": {
                  "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
                  "40%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", flex: 1 }}>
              {label}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                color: "text.secondary",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 120ms ease",
              }}
            />
          </ButtonBase>
          <Collapse in={expanded} timeout={160}>
            <Box
              id={bodyId}
              ref={bodyRef}
              sx={{
                px: 1.25,
                py: 1,
                maxHeight: 140,
                overflowY: "auto",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              {content ? (
                <MarkdownContent sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.55 }}>
                  {content}
                </MarkdownContent>
              ) : (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Thinking...</Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}

export function ThinkingIndicator({ agentName, agentId, onOpenAgentSettings, label, messages }) {
  const derivedLabel = label || (messages ? deriveToolLabelFromParts(messages) : null);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const elapsed = useElapsedSeconds();
  const elapsedLabel = `(${formatElapsed(elapsed)})`;
  return (
    <Box sx={{ ...messageRowSx, pt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <AgentAvatar name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        {derivedLabel ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              pt: "8px",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                animation: "toolColorPulse 3s infinite ease-in-out",
                flexShrink: 0,
                "@keyframes toolColorPulse": {
                  "0%": { backgroundColor: "#3b82f6", transform: "scale(0.85)" },
                  "25%": { backgroundColor: "#8b5cf6", transform: "scale(1.1)" },
                  "50%": { backgroundColor: "#ec4899", transform: "scale(0.85)" },
                  "75%": { backgroundColor: "#f59e0b", transform: "scale(1.1)" },
                  "100%": { backgroundColor: "#3b82f6", transform: "scale(0.85)" },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {derivedLabel} {elapsedLabel}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{ display: "flex", gap: 0.75, alignItems: "center", pt: "7px" }}
          >
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: "text.disabled",
                    animation: "agentPulse 1.4s infinite ease-in-out",
                    animationDelay: `${i * 0.2}s`,
                    "@keyframes agentPulse": {
                      "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
                      "40%": { opacity: 1, transform: "scale(1)" },
                    },
                  }}
                />
              ))}
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {elapsedLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function StoppedIndicator() {
  return (
    <Box sx={{ ...messageRowSx, py: 0.4 }}>
      <Box sx={{ pl: TEXT_INDENT, display: "flex", alignItems: "center", gap: 0.6 }}>
        <PauseCircleOutlineIcon sx={{ fontSize: 13, color: "text.disabled" }} />
        <Typography
          sx={{ fontSize: 11.5, color: "text.disabled", fontStyle: "italic" }}
        >
          Response stopped
        </Typography>
      </Box>
    </Box>
  );
}

export function ThinkingAgentsPanel({
  thinkingAgents,
  streamingDeltas,
  onOpenAgentSettings,
}) {
  if (!Array.isArray(thinkingAgents) || thinkingAgents.length === 0) return null;
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {thinkingAgents.map((agent) => {
        const streamedContent = streamingDeltas?.[agent.agentId] || "";
        const agentName = agent.agentName || "Agent";
        const avatarClick = getAgentAvatarClickHandler(
          agent.agentId,
          onOpenAgentSettings
        );
        return (
          <Box
            key={agent.agentId || agent.agentName}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <AgentAvatar name={agentName} size={AVATAR_SIZE} onClick={avatarClick} sx={{ mt: "2px" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {agentName}
              </Typography>
              {streamedContent ? (
                <MarkdownContent
                  sx={{
                    fontSize: 14,
                    color: "text.primary",
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                    mt: 0.25,
                  }}
                >
                  {streamedContent}
                </MarkdownContent>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <CircularProgress
                    size={12}
                    thickness={6}
                    sx={{ color: "text.secondary" }}
                  />
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Generating response...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export function ExecutionFallbackIndicator({ label = "Processing" }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 0.9,
      }}
    >
      <CircularProgress
        size={12}
        thickness={6}
        sx={{ color: "text.secondary", flexShrink: 0 }}
      />
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}...</Typography>
    </Box>
  );
}

export function ConnectionStatusBanner({ connectionState, streamError }) {
  if (connectionState === "reconnecting") {
    return (
      <Box
        sx={{
          mx: 2.5,
          mb: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: "8px",
          backgroundColor: "warning.main",
          color: "warning.contrastText",
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: 13,
        }}
      >
        <CircularProgress size={12} thickness={6} sx={{ color: "inherit", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>Reconnecting...</Typography>
      </Box>
    );
  }
  if (connectionState === "disconnected" || streamError) {
    return (
      <Box
        sx={{
          mx: 2.5,
          mb: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: "8px",
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(244,67,54,0.15)" : "rgba(244,67,54,0.08)",
          color: "error.main",
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: 13,
        }}
      >
        <WifiOffIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>
          {streamError || "Connection lost. Refresh to reconnect."}
        </Typography>
      </Box>
    );
  }
  return null;
}

export function SendErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(244,67,54,0.15)" : "rgba(244,67,54,0.08)",
        color: "error.main",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "inherit", fontWeight: 500, flex: 1 }}>
        {error}
      </Typography>
      {onDismiss && (
        <IconButton size="small" onClick={onDismiss} sx={{ color: "inherit", p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}

export function SessionErrorBanner({ status, statusReason }) {
  if (status !== "ERROR") return null;
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(244,67,54,0.15)" : "rgba(244,67,54,0.08)",
        color: "error.main",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>
        {statusReason || "Session encountered an error."}
      </Typography>
    </Box>
  );
}

export function WaitingForWorkersBanner({ status, workerCount }) {
  if (status !== "WAITING_FOR_WORKERS") return null;
  const label = workerCount
    ? `Waiting for workers \u2014 ${workerCount} blocking worker${workerCount === 1 ? "" : "s"} in progress`
    : "Waiting for workers to complete\u2026";
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(139, 92, 246, 0.12)"
            : "rgba(139, 92, 246, 0.08)",
        color: "rgb(139, 92, 246)",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <PauseCircleOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}
